import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import gsap from 'gsap';
import { Scene } from '../engine/scene';
import { getContext } from '../engine/context';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/tuning';
import { SKINS, type SkinDefinition } from '../config/skins';
import { WalletStore } from '../stores/WalletStore';
import { CoinBalance } from '../ui/CoinBalance';
import { SkinCard } from '../ui/SkinCard';
import { onKeyDown } from '../engine/input';

type TabId = 'skins' | 'powerups';

/**
 * ShopScene — single tab ("Skins") today. The tabs row has a disabled
 * "Power-ups — Soon" entry to signal future expansion. Layout is anchored
 * to the 1600×900 design space.
 */
export class ShopScene extends Scene {
  private coinBalance!: CoinBalance;
  private skinCards: SkinCard[] = [];
  private activeTab: TabId = 'skins';
  private skinsContainer!: Container;
  private powerupsContainer!: Container;

  async onEnter(): Promise<void> {
    const ctx = getContext();
    const cx = GAME_WIDTH / 2;

    // ── Top bar: back + coin balance ────────────────────────────────────────
    const back = new Text({
      text: '‹ BACK',
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 32,
        fill: 0x5fdbcf,
        stroke: { color: 0x1a0d22, width: 3 },
      },
    });
    back.anchor.set(0, 0.5);
    back.position.set(50, 50);
    back.eventMode = 'static';
    back.cursor = 'pointer';
    back.on('pointerover', () => { back.style.fill = 0xffd23f; });
    back.on('pointerout', () => { back.style.fill = 0x5fdbcf; });
    back.on('pointerdown', () => {
      ctx.audio.play('sfx_click', { volume: 0.55 });
      ctx.sceneManager.start('MenuScene');
    });
    this.addChild(back);

    this.coinBalance = new CoinBalance({
      x: GAME_WIDTH - 200,
      y: 50,
      scale: 1.1,
      fontSize: 34,
      animatePopups: true,
    });
    this.addChild(this.coinBalance);

    // ── Header ──────────────────────────────────────────────────────────────
    const header = new Text({
      text: 'SHOP',
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 72,
        fill: 0xffd23f,
        stroke: { color: 0x1a0d22, width: 5 },
        letterSpacing: 6,
      },
    });
    header.anchor.set(0.5);
    header.position.set(cx, 120);
    this.addChild(header);

    const sub = new Text({
      text: '— choose your smoking buddy —',
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 20,
        fill: 0x88e8db,
        fontStyle: 'italic',
      },
    });
    sub.anchor.set(0.5);
    sub.position.set(cx, 168);
    this.addChild(sub);

    // ── Tabs ────────────────────────────────────────────────────────────────
    this.buildTabs(cx, 215);

    // ── Containers for tab content ──────────────────────────────────────────
    this.skinsContainer = new Container();
    this.addChild(this.skinsContainer);
    this.powerupsContainer = new Container();
    this.powerupsContainer.visible = false;
    this.addChild(this.powerupsContainer);

    this.buildSkinsTab();
    this.buildPowerupsPlaceholder();

    // ── Keyboard: Escape back to menu ──────────────────────────────────────
    this.onCleanup(onKeyDown('Escape', () => ctx.sceneManager.start('MenuScene')));
  }

  private buildTabs(cx: number, y: number): void {
    const ctx = getContext();
    const mkTab = (label: string, id: TabId, x: number, enabled: boolean): void => {
      const container = new Container();
      container.position.set(x, y);

      const bg = new Graphics();
      const w = 200;
      const h = 48;
      bg.roundRect(-w / 2, -h / 2, w, h, 12)
        .fill({ color: id === this.activeTab ? 0x5fdbcf : 0x1a0d22, alpha: id === this.activeTab ? 0.28 : 0.55 })
        .stroke({ color: id === this.activeTab ? 0x5fdbcf : 0x44424a, width: 2 });
      container.addChild(bg);

      const text = new Text({
        text: label,
        style: {
          fontFamily: 'Impact, Arial Black, sans-serif',
          fontSize: 22,
          fill: enabled ? (id === this.activeTab ? 0xffd23f : 0x5fdbcf) : 0x88889a,
          letterSpacing: 3,
        },
      });
      text.anchor.set(0.5);
      text.position.set(enabled ? 0 : -20, 0);
      container.addChild(text);

      if (!enabled) {
        const soon = new Sprite(Texture.from('ui_badge_soon'));
        soon.anchor.set(0.5);
        soon.position.set(65, 0);
        soon.scale.set(0.7);
        container.addChild(soon);
      }

      if (enabled) {
        container.eventMode = 'static';
        container.cursor = 'pointer';
        container.on('pointerdown', () => {
          if (this.activeTab === id) return;
          ctx.audio.play('sfx_click', { volume: 0.45 });
          this.activeTab = id;
          this.skinsContainer.visible = id === 'skins';
          this.powerupsContainer.visible = id === 'powerups';
          // Rebuild tabs with updated active highlight.
          this.rebuildTabs(cx, y);
        });
      }

      this.addChild(container);
      this.tabContainers.push(container);
    };

    mkTab('SKINS', 'skins', cx - 120, true);
    mkTab('POWER-UPS', 'powerups', cx + 120, false);
  }

  private tabContainers: Container[] = [];

  private rebuildTabs(cx: number, y: number): void {
    this.tabContainers.forEach((c) => {
      this.removeChild(c);
      c.destroy({ children: true });
    });
    this.tabContainers = [];
    this.buildTabs(cx, y);
  }

  private buildSkinsTab(): void {
    const cx = GAME_WIDTH / 2;
    const cardY = GAME_HEIGHT / 2 + 80;
    // 4 cards in a row: SkinCard frames are ~280 wide after inner scale 1.0.
    // Use spacing 320 → 4 × 320 = 1280; centered on 1600 canvas leaves 160 margin.
    const spacing = 320;
    const startX = cx - (spacing * (SKINS.length - 1)) / 2;

    SKINS.forEach((skin, i) => {
      const card = new SkinCard({
        x: startX + i * spacing,
        y: cardY,
        skin,
        audio: getContext().audio,
        onAction: (s, action) => this.handleCardAction(s, action),
      });
      this.skinCards.push(card);
      this.skinsContainer.addChild(card);
    });
  }

  private buildPowerupsPlaceholder(): void {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2 + 60;
    const text = new Text({
      text: 'POWER-UPS COMING SOON',
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 48,
        fill: 0x88889a,
        stroke: { color: 0x1a0d22, width: 3 },
        letterSpacing: 4,
      },
    });
    text.anchor.set(0.5);
    text.position.set(cx, cy);
    this.powerupsContainer.addChild(text);

    const sub = new Text({
      text: 'Boosts, consumables, and surprises.\nKeep playing — stay tuned.',
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 20,
        fill: 0x5fdbcf,
        align: 'center',
      },
    });
    sub.anchor.set(0.5);
    sub.position.set(cx, cy + 60);
    this.powerupsContainer.addChild(sub);
  }

  private handleCardAction(skin: SkinDefinition, action: 'buy' | 'equip'): void {
    const ctx = getContext();
    if (action === 'equip') {
      WalletStore.selectSkin(skin.id);
      this.refreshAllCards();
      return;
    }
    // Buy
    const result = WalletStore.purchase(skin.id);
    if (!result.ok) {
      ctx.audio.play('sfx_click', { volume: 0.3 });
      return;
    }
    // Purchase succeeded — animate coins, refresh cards, play a happier cue.
    ctx.audio.play('sfx_click', { volume: 0.8 });
    this.coinBalance.refresh();
    // Small purchase burst: fade in a "–PRICE" popup on the balance.
    const popup = new Text({
      text: `-${skin.price.toLocaleString('en-US')}`,
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 26,
        fill: 0xff6b7a,
        stroke: { color: 0x1a0d22, width: 2 },
      },
    });
    popup.anchor.set(0.5);
    popup.position.set(this.coinBalance.x, this.coinBalance.y + 40);
    this.addChild(popup);
    gsap.to(popup, { y: popup.y + 40, alpha: 0, duration: 0.9, ease: 'power2.in',
      onComplete: () => { this.removeChild(popup); popup.destroy(); } });

    this.refreshAllCards();
  }

  private refreshAllCards(): void {
    this.coinBalance.refresh();
    this.skinCards.forEach((c) => c.refresh());
  }
}
